/**
 * AlternativesScreen - Hier laten we dingen zien die beter zijn voor de wereld
 */

import React, {useState, useEffect} from 'react';
import {View, Text, StyleSheet, FlatList, TouchableOpacity, Platform, Image} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useTranslation} from 'react-i18next';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import type {AllStackParamList} from '../models/Navigation';
import type {ProductDisplay} from '../models';
import {productService} from '../services';
import {WaveHeader, LoadingSpinner, ErrorDisplay} from '../components';

type Props = NativeStackScreenProps<AllStackParamList, 'Alternatives'>;

export const AlternativesScreen: React.FC<Props> = ({navigation, route}) => {
  const {t} = useTranslation();
  const {productId, currentGrade} = route.params;

  const [alternatieven, setAlternatieven] = useState<ProductDisplay[]>([]);
  const [aanHetLaden, setAanHetLaden] = useState(true);
  const [foutmelding, setFoutmelding] = useState<string | null>(null);

  // Alternatieve producten laden (want het huidige is blijkbaar troep)
  useEffect(() => {
    laadAlternatieven();
  }, [productId]);

  const laadAlternatieven = async () => {
    try {
      setAanHetLaden(true);
      setFoutmelding(null);
      const alternatieveProducten = await productService.getAlternatives(productId);
      
      // sorteren op cijfer (beste eerst) - service doet dit al
      setAlternatieven(alternatieveProducten);
    } catch (err) {
      console.error('Error loading alternatives:', err);
      setFoutmelding(t('errors.databaseError'));
    } finally {
      setAanHetLaden(false);
    }
  };

  const behandelProductKlik = (product: ProductDisplay) => {
    navigation.push('ProductDetail', {
      productId: product.id,
    });
  };

  const bepaalCijferKleur = (grade: string): string => {
    const cijferKleuren: Record<string, string> = {
      'A+': '#22C55E', 'A': '#22C55E', 'A-': '#22C55E',  // Green
      'B+': '#84CC16', 'B': '#84CC16', 'B-': '#84CC16',  // Light green
      'C+': '#EAB308', 'C': '#EAB308', 'C-': '#EAB308',  // Yellow
      'D+': '#F97316', 'D': '#F97316', 'D-': '#F97316',  // Orange
      'E+': '#EF4444', 'E': '#EF4444', 'E-': '#EF4444',  // Red
    };
    return cijferKleuren[grade] || '#999';
  };

  const AlternativeCard = React.memo(({ item, onPress }: { item: ProductDisplay, onPress: () => void }) => {
    const { t } = useTranslation();
    const [imageError, setImageError] = useState(false);
    
    return (
      <TouchableOpacity
        style={stijlen.productKaart}
        onPress={onPress}
        accessibilityLabel={`${item.name} ${t('alternatives.betterGrade')}`}
        accessibilityRole="button">
        <View style={stijlen.kaartInhoud}>
          <View style={stijlen.afbeeldingBak}>
            {item.imageUrl && !imageError ? (
                <Image
                  source={{ uri: item.imageUrl }}
                  style={{ width: 80, height: 80, borderRadius: 12, backgroundColor: '#F1F5F9' }}
                  resizeMode="cover"
                  onError={() => setImageError(true)}
                />
            ) : (
                <View style={stijlen.afbeeldingPlaatsvervanger}>
                  <Text style={stijlen.afbeeldingPlaatsvervangerTekst}>🌊</Text>
                </View>
            )}
          </View>

          <View style={stijlen.productInfo}>
            <Text style={stijlen.productNaam} numberOfLines={2}>
              {item.name}
            </Text>
            <Text style={stijlen.merkNaam} numberOfLines={1}>
              {item.brand}
            </Text>
            
            {item.sustainabilityFactors.length > 0 && (
              <View style={stijlen.kenmerkenBak}>
                {item.sustainabilityFactors.slice(0, 2).map((factor, index) => (
                  <View key={index} style={stijlen.kenmerkItem}>
                    <Text style={stijlen.kenmerkIcoon}>{factor.icon}</Text>
                    <Text style={stijlen.kenmerkTekst} numberOfLines={1}>
                      {factor.label}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>

          <View style={stijlen.cijferBak}>
            <View
              style={[
                stijlen.cijferBadge,
                {backgroundColor: bepaalCijferKleur(item.grade)},
              ]}>
              <Text style={stijlen.cijferTekst}>{item.grade}</Text>
            </View>
            <Text style={stijlen.beterCijferLabel}>
              {t('alternatives.betterGrade')}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  });

  const toonProductKaart = React.useCallback(({item}: {item: ProductDisplay}) => (
    <AlternativeCard item={item} onPress={() => behandelProductKlik(item)} />
  ), [behandelProductKlik]);

  const toonLegeStaat = () => (
    <View style={stijlen.legeStaat}>
      <Text style={stijlen.leegIcoon}>🔍</Text>
      <Text style={stijlen.leegTitel}>{t('alternatives.noAlternatives')}</Text>
      <Text style={stijlen.leegBeschrijving}>
        {t('alternatives.description')}
      </Text>
    </View>
  );

  // Even geduld...
  if (aanHetLaden) {
    return (
      <SafeAreaView style={stijlen.container} edges={['top']}>
        <WaveHeader showLogo={true} height={180} />
        <LoadingSpinner message={t('common.loading')} />
      </SafeAreaView>
    );
  }

  // Er is iets fout gegaan
  if (foutmelding) {
    return (
      <SafeAreaView style={stijlen.container} edges={['top']}>
        <WaveHeader showLogo={true} height={180} />
        <ErrorDisplay
          message={foutmelding}
          onRetry={laadAlternatieven}
          showRetry={true}
        />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[stijlen.container, Platform.OS === 'web' && stijlen.webContainer]} edges={['top']}>
      {/* Header met logo en terug knop */}
      <View style={stijlen.kopNavigatie}>
        <WaveHeader showLogo={true} height={180} />
        
        {/* Terug Knop */}
        <TouchableOpacity
          style={stijlen.terugKnop}
          onPress={() => navigation.goBack()}
          accessibilityLabel={t('common.back')}
          accessibilityRole="button">
          <Text style={stijlen.terugKnopTekst}>←</Text>
        </TouchableOpacity>
      </View>

      {/* Inhoud */}
      <View style={stijlen.inhoud}>
        {/* Titel en uitleg */}
        <View style={stijlen.titelBak}>
          <Text style={stijlen.titel}>{t('alternatives.title')}</Text>
          <Text style={stijlen.beschrijving}>
            {t('alternatives.description')}
          </Text>
        </View>

        {/* Lijst met alternatieven */}
        <FlatList
          data={alternatieven}
          renderItem={toonProductKaart}
          keyExtractor={item => item.id.toString()}
          contentContainerStyle={stijlen.lijstInhoud}
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={toonLegeStaat}
          initialNumToRender={5}
          maxToRenderPerBatch={5}
          windowSize={5}
          removeClippedSubviews={true}
          updateCellsBatchingPeriod={50}
        />
      </View>
    </SafeAreaView>
  );
};

// Hulpfunctie voor kleurtjes verwijderd (nu in component) -> actually better keep it or move it up, I duplicated it inside component scope for ease but `bepaalCijferKleur` on line 187 is outside compScope. I should just use the external one in my new component.

// Removing duplicate definition from replace block


const stijlen = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F5',
  },
  webContainer: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
    // @ts-ignore - Web schaduw
    boxShadow: '0 0 20px rgba(0,0,0,0.1)',
    minHeight: Platform.OS === 'web' ? ('100vh' as any) : '100%',
  },
  kopNavigatie: {
    position: 'relative',
  },
  terugKnop: {
    position: 'absolute',
    top: 16,
    left: 16,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    zIndex: 10,
  },
  terugKnopTekst: {
    fontSize: 24,
    color: '#7B3FF2',
    fontWeight: 'bold',
  },
  inhoud: {
    flex: 1,
    paddingHorizontal: 16,
  },
  titelBak: {
    paddingVertical: 16,
  },
  titel: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  beschrijving: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  lijstInhoud: {
    paddingBottom: 16,
  },
  productKaart: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  kaartInhoud: {
    flexDirection: 'row',
    padding: 16,
    alignItems: 'center',
  },
  afbeeldingBak: {
    marginRight: 16,
  },
  afbeeldingPlaatsvervanger: {
    width: 80,
    height: 80,
    borderRadius: 12,
    backgroundColor: '#E8F4F8',
    justifyContent: 'center',
    alignItems: 'center',
  },
  afbeeldingPlaatsvervangerTekst: {
    fontSize: 32,
  },
  productInfo: {
    flex: 1,
    marginRight: 12,
  },
  productNaam: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  merkNaam: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
  },
  kenmerkenBak: {
    flexDirection: 'column',
    gap: 4,
  },
  kenmerkItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  kenmerkIcoon: {
    fontSize: 14,
    marginRight: 6,
  },
  kenmerkTekst: {
    fontSize: 12,
    color: '#666',
    flex: 1,
  },
  cijferBak: {
    alignItems: 'center',
  },
  cijferBadge: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.15,
    shadowRadius: 3,
    elevation: 3,
  },
  cijferTekst: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  beterCijferLabel: {
    fontSize: 10,
    color: '#666',
    marginTop: 4,
    textAlign: 'center',
  },
  legeStaat: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    paddingVertical: 64,
  },
  leegIcoon: {
    fontSize: 64,
    marginBottom: 16,
  },
  leegTitel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
    textAlign: 'center',
  },
  leegBeschrijving: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    lineHeight: 20,
  },
});
